import React, { useState } from 'react';
import { Shield, BookOpen, AlertTriangle, CheckCircle, FileText, Lock, X, ChevronRight, Activity, Globe, Scale } from 'lucide-react';
import { useEligibilityLimit } from '../../hooks/useEligibilityLimit';
import { useNavigate } from 'react-router-dom';
import AIProcessingLab from '../shared/AIProcessingLab';
import AdvisorContactForm from '../shared/AdvisorContactForm';


type ComplianceType = 'student' | 'visa' | null;

// --- Student Compliance Types ---
type StudentPassportValid = '12+ months' | '6-11 months' | 'less than 6 months' | 'Expired';
type StudentEnglish = 'Yes, I have taken it' | 'Not yet' | 'Planning to take it';
type StudentFunds = 'Self-funded' | 'Parent/Guardian' | 'Sponsor' | 'Education Loan';
type StudentCountry = 'UK' | 'USA' | 'Canada' | 'Australia' | 'New Zealand' | 'Europe (Schengen)';
type YesNo = 'Yes' | 'No';

interface StudentForm {
    passport: StudentPassportValid;
    english: StudentEnglish;
    funds: StudentFunds;
    country: StudentCountry;
    refusal: YesNo;
}

// --- Visa Compliance Types ---
type VisaCountry = 'United Kingdom' | 'USA' | 'Australia' | 'Europe (Schengen)' | 'Ireland' | 'Malaysia' | 'Dubai / UAE';
type VisaTimeline = '12+ months' | '6-12 months' | '3-6 months' | 'Less than 3 months' | 'Expiring soon / expired' | 'Not sure';

interface VisaForm {
    country: VisaCountry;
    region: string;
    category: string;
    timeline: VisaTimeline;
    refusal: YesNo;
}

const VisaComplianceHub: React.FC = () => {
    const navigate = useNavigate();
    const { canAccess, loading: authLoading, markAsUsed } = useEligibilityLimit('immigration');
    const [activeTab, setActiveTab] = useState<ComplianceType>('student');
    const [showResult, setShowResult] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form States
    const [studentForm, setStudentForm] = useState<StudentForm>({
        passport: '12+ months',
        english: 'Not yet',
        funds: 'Self-funded',
        country: 'UK',
        refusal: 'No'
    });

    const [visaForm, setVisaForm] = useState<VisaForm>({
        country: 'United Kingdom',
        region: '',
        category: '',
        timeline: '12+ months',
        refusal: 'No'
    });

    // API Result States
    const [studentResult, setStudentResult] = useState<any>(null);
    const [visaResult, setVisaResult] = useState<any>(null);

    // --- Submission State moved to top to comply with Rules of Hooks ---
    const [showSubmitForm, setShowSubmitForm] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Personal Details for Submission
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+44');

    // --- Mock Data Helpers ---
    const getRegions = (country: VisaCountry) => {
        switch (country) {
            case 'United Kingdom': return ['London', 'Birmingham', 'Manchester', 'Leeds', 'Glasgow', 'Edinburgh', 'Cardiff', 'Belfast', 'Other'];
            case 'USA': return ['All 50 states'];
            case 'Australia': return ['NSW', 'Victoria', 'Queensland', 'WA', 'SA', 'Tasmania', 'ACT', 'NT'];
            case 'Europe (Schengen)': return ['Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Sweden', 'Other'];
            case 'Ireland': return ['Dublin', 'Cork', 'Galway', 'Limerick', 'Waterford', 'Other'];
            case 'Malaysia': return ['All states + federal territories'];
            case 'Dubai / UAE': return ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Fujairah', 'Ras Al Khaimah', 'Umm Al Quwain'];
            default: return [];
        }
    };

    const getVisaCategories = (country: VisaCountry) => {
        switch (country) {
            case 'United Kingdom': return ['Student Visa', 'Graduate Route', 'Skilled Worker', 'Health & Care Worker', 'Family Route', 'Innovator Founder', 'Visitor Visa', 'Other'];
            case 'USA': return ['F-1 Student', 'J-1 Exchange Visitor', 'H-1B Specialty Worker', 'O-1 Extraordinary Ability', 'B-2 Visitor', 'Other'];
            case 'Australia': return ['Student Visa', '485 Graduate Visa', 'Skilled Migration', 'Visitor Visa', 'Partner Visa', 'Other'];
            case 'Europe (Schengen)': return ['Student Visa', 'Work Permit', 'Job Seeker Visa', 'Family Reunification', 'Schengen Visitor Visa'];
            case 'Ireland': return ['Stamp 2 (Student)', 'Stamp 1G (Graduate)', 'Critical Skills Employment Permit', 'General Employment Permit', 'Visitor Visa'];
            case 'Malaysia': return ['Student Pass', 'Employment Pass', 'Dependant Pass', 'MM2H', 'Visitor Visa'];
            case 'Dubai / UAE': return ['Student Visa', 'Employment Visa', 'Golden Visa', 'Investor Visa', 'Family Visa', 'Visit Visa'];
            default: return [];
        }
    };

    const handleRunPrediction = async () => {
        setLoading(true);
        setError(null);
        setShowResult(false);

        try {
            const endpoint = '/api/predict-visa';
            const payload = {
                type: activeTab,
                data: activeTab === 'student' ? studentForm : visaForm
            };

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Failed to generate prediction');
            }

            const data = await response.json();

            if (activeTab === 'student') {
                setStudentResult(data);
            } else {
                setVisaResult(data);
            }
            setShowResult(true);
            markAsUsed(); // Mark as used on successful result

        } catch (err) {
            console.error(err);
            setError('AI Analysis failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) return <div className="p-8 text-center text-slate-400">Loading Access Rights...</div>;

    if (!canAccess) {
        return (
            <div className="flex flex-col items-center justify-center text-center p-8 space-y-6 bg-white rounded-xl shadow-lg min-h-[400px]">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                    <Lock size={32} className="text-gray-400" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-[#011E26]">Free Compliance Check Limit Reached</h3>
                    <p className="text-slate-500 mt-2 max-w-md mx-auto">
                        You have already used your free visa compliance check. Please sign in or create an account to access unlimited AI assessments and legal support.
                    </p>
                </div>
                <div className="flex gap-4">
                    <button
                        onClick={() => navigate('/login')}
                        className="px-6 py-3 bg-white border border-gray-200 text-[#011E26] font-bold rounded hover:border-[#009B8F] transition-all"
                    >
                        Sign In
                    </button>
                    <button
                        onClick={() => navigate('/signup')}
                        className="px-6 py-3 bg-[#009B8F] text-white font-bold rounded hover:bg-[#00857a] transition-all"
                    >
                        Create Free Account
                    </button>
                </div>
            </div>
        );
    }

    const handleFinalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log("Submitting Visa Compliance Form...");
        setSubmitLoading(true);

        const formData = new FormData();
        formData.append('service', 'visa');
        formData.append('name', name);
        formData.append('email', email);
        formData.append('phone', countryCode + phone);

        // Add existing form data
        if (activeTab === 'student') {
            formData.append('type', 'student_compliance');
            formData.append('passport', studentForm.passport);
            formData.append('english', studentForm.english);
            formData.append('funds', studentForm.funds);
            formData.append('country', studentForm.country);
            formData.append('refusal', studentForm.refusal);
            if (studentResult) {
                formData.append('ai_score', studentResult.score.toString());
                formData.append('ai_risk', studentResult.riskLevel);
            }
        } else {
            formData.append('type', 'visa_compliance');
            formData.append('country', visaForm.country);
            formData.append('region', visaForm.region);
            formData.append('category', visaForm.category);
            formData.append('timeline', visaForm.timeline);
            formData.append('refusal', visaForm.refusal);
            if (visaResult) {
                formData.append('ai_score', visaResult.score.toString());
                formData.append('ai_risk', visaResult.riskLevel);
            }
        }

        try {
            const response = await fetch('/api/eligibility', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                setSubmitted(true);
            }
        } catch (error) {
            console.error("Submission failed", error);
        } finally {
            setSubmitLoading(false);
        }
    };

    if (showResult && submitted) {
        return (
            <div className="w-full bg-white rounded-xl overflow-hidden flex flex-col md:flex-row relative p-8 items-center justify-center text-center">
                <div className="max-w-lg">
                    <div className="w-20 h-20 bg-[#009B8F]/10 text-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle size={40} />
                    </div>
                    <h3 className="text-2xl font-serif text-[#011E26] mb-2">Compliance Report Sent</h3>
                    <p className="text-slate-600 mb-8">
                        Our immigration team has received your compliance assessment. We will review the potential risks and contact you with a strategy.
                    </p>
                    <button
                        onClick={() => {
                            setShowResult(false);
                            setSubmitted(false);
                            setShowSubmitForm(false);
                            setStudentResult(null);
                            setVisaResult(null);
                        }}
                        className="text-[#009B8F] font-bold uppercase tracking-widest text-xs hover:underline"
                    >
                        Start New Check
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-white rounded-xl overflow-hidden flex flex-col md:flex-row relative">

            {/* --- LEFT SIDEBAR --- */}
            <div className="w-full md:w-1/3 bg-gray-50 border-r border-gray-200 p-6 flex flex-col">
                <div className="mb-8">
                    <div className="flex items-center gap-2 text-[#009B8F] mb-2">
                        <Shield size={24} />
                        <span className="font-bold tracking-widest text-sm">VISA COMPLIANCE HUB</span>
                    </div>
                    <h2 className="text-2xl font-serif text-[#011E26] mb-2">Smart Compliance Checks</h2>
                    <p className="text-slate-600 text-sm">Automated risk assessment for students and visa holders.</p>
                </div>

                <div className="space-y-4 flex-grow">
                    <button
                        onClick={() => { setActiveTab('student'); setShowResult(false); }}
                        className={`w-full text-left p-4 rounded-lg border transition-all flex items-center gap-3 ${activeTab === 'student'
                            ? 'bg-[#009B8F] border-[#009B8F] text-white shadow-lg'
                            : 'bg-white border-gray-200 text-slate-600 hover:border-[#009B8F] hover:text-[#009B8F]'
                            }`}
                    >
                        <BookOpen size={20} className={activeTab === 'student' ? 'text-white' : 'text-slate-400 group-hover:text-[#009B8F]'} />
                        <div>
                            <div className="font-medium">Student Compliance Check</div>
                            <div className={`text-xs mt-1 ${activeTab === 'student' ? 'text-white/80' : 'text-slate-400'}`}>For students applying or studying</div>
                        </div>
                    </button>

                    <button
                        onClick={() => { setActiveTab('visa'); setShowResult(false); }}
                        className={`w-full text-left p-4 rounded-lg border transition-all flex items-center gap-3 ${activeTab === 'visa'
                            ? 'bg-[#009B8F] border-[#009B8F] text-white shadow-lg'
                            : 'bg-white border-gray-200 text-slate-600 hover:border-[#009B8F] hover:text-[#009B8F]'
                            }`}
                    >
                        <Globe size={20} className={activeTab === 'visa' ? 'text-white' : 'text-slate-400 group-hover:text-[#009B8F]'} />
                        <div>
                            <div className="font-medium">Visa-Related Compliance Check</div>
                            <div className={`text-xs mt-1 ${activeTab === 'visa' ? 'text-white/80' : 'text-slate-400'}`}>For visa switching & timelines</div>
                        </div>
                    </button>
                </div>

                <div className="mt-8 p-4 bg-orange-50 border border-orange-200 rounded text-orange-800 text-xs leading-relaxed">
                    <div className="flex items-center gap-2 mb-2 font-bold text-orange-700">
                        <AlertTriangle size={14} /> Important Notice
                    </div>
                    Nex AI does not provide legal advice. Our system performs an AI-powered compliance check based solely on the information you provide.
                </div>
            </div>

            {/* --- MAIN CONTENT AREA --- */}
            <div className="w-full md:w-2/3 bg-white p-8">

                {loading ? (
                    <AIProcessingLab text="Analyzing Compliance Patterns..." />
                ) : error ? (
                    <div className="h-full flex flex-col items-center justify-center space-y-6 text-center">
                        <AlertTriangle className="text-red-500" size={48} />
                        <h3 className="text-xl text-[#011E26] font-serif">{error}</h3>
                        <button onClick={() => setError(null)} className="text-[#009B8F] underline">Try Again</button>
                    </div>
                ) : !showResult ? (
                    <div className="max-w-2xl mx-auto animate-fadeIn">
                        <h2 className="text-3xl font-serif text-[#011E26] mb-6">
                            {activeTab === 'student' ? 'Student Profile Assessment' : 'Visa Status Assessment'}
                        </h2>

                        {/* STUDENT FORM */}
                        {activeTab === 'student' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Passport Validity</label>
                                        <select
                                            value={studentForm.passport}
                                            onChange={(e) => setStudentForm({ ...studentForm, passport: e.target.value as StudentPassportValid })}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded text-[#011E26] focus:border-[#009B8F] focus:outline-none"
                                        >
                                            <option>12+ months</option>
                                            <option>6-11 months</option>
                                            <option>less than 6 months</option>
                                            <option>Expired</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">English Test Status</label>
                                        <select
                                            value={studentForm.english}
                                            onChange={(e) => setStudentForm({ ...studentForm, english: e.target.value as StudentEnglish })}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded text-[#011E26] focus:border-[#009B8F] focus:outline-none"
                                        >
                                            <option>Yes, I have taken it</option>
                                            <option>Not yet</option>
                                            <option>Planning to take it</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Financial Readiness</label>
                                        <select
                                            value={studentForm.funds}
                                            onChange={(e) => setStudentForm({ ...studentForm, funds: e.target.value as StudentFunds })}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded text-[#011E26] focus:border-[#009B8F] focus:outline-none"
                                        >
                                            <option>Self-funded</option>
                                            <option>Parent/Guardian</option>
                                            <option>Sponsor</option>
                                            <option>Education Loan</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Destination Country</label>
                                        <select
                                            value={studentForm.country}
                                            onChange={(e) => setStudentForm({ ...studentForm, country: e.target.value as StudentCountry })}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded text-[#011E26] focus:border-[#009B8F] focus:outline-none"
                                        >
                                            <option>UK</option>
                                            <option>USA</option>
                                            <option>Canada</option>
                                            <option>Australia</option>
                                            <option>New Zealand</option>
                                            <option>Europe (Schengen)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2 pt-4 border-t border-gray-100">
                                    <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider flex items-center gap-2">
                                        <AlertTriangle size={16} className="text-orange-500" />
                                        Previous Visa Refusal?
                                    </label>
                                    <div className="flex gap-4">
                                        <label className={`flex-1 p-3 rounded border cursor-pointer flex items-center justify-center gap-2 transition-all ${studentForm.refusal === 'No' ? 'bg-[#009B8F] border-[#009B8F] text-white' : 'bg-gray-50 border-gray-200 text-slate-500'}`}>
                                            <input type="radio" name="refusal" className="hidden" onClick={() => setStudentForm({ ...studentForm, refusal: 'No' })} />
                                            <CheckCircle size={16} /> No
                                        </label>
                                        <label className={`flex-1 p-3 rounded border cursor-pointer flex items-center justify-center gap-2 transition-all ${studentForm.refusal === 'Yes' ? 'bg-red-500 border-red-500 text-white' : 'bg-gray-50 border-gray-200 text-slate-500'}`}>
                                            <input type="radio" name="refusal" className="hidden" onClick={() => setStudentForm({ ...studentForm, refusal: 'Yes' })} />
                                            <AlertTriangle size={16} /> Yes
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* VISA FORM */}
                        {activeTab === 'visa' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Select Country</label>
                                        <select
                                            value={visaForm.country}
                                            onChange={(e) => setVisaForm({ ...visaForm, country: e.target.value as VisaCountry, region: '', category: '' })}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded text-[#011E26] focus:border-[#009B8F] focus:outline-none"
                                        >
                                            <option>United Kingdom</option>
                                            <option>USA</option>
                                            <option>Australia</option>
                                            <option>Europe (Schengen)</option>
                                            <option>Ireland</option>
                                            <option>Malaysia</option>
                                            <option>Dubai / UAE</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Region / State</label>
                                        <select
                                            value={visaForm.region}
                                            onChange={(e) => setVisaForm({ ...visaForm, region: e.target.value })}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded text-[#011E26] focus:border-[#009B8F] focus:outline-none"
                                        >
                                            <option value="">Select Region</option>
                                            {getRegions(visaForm.country).map(r => <option key={r}>{r}</option>)}
                                        </select>
                                    </div>

                                    <div className="space-y-2 md:col-span-2">
                                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Visa Category</label>
                                        <select
                                            value={visaForm.category}
                                            onChange={(e) => setVisaForm({ ...visaForm, category: e.target.value })}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded text-[#011E26] focus:border-[#009B8F] focus:outline-none"
                                        >
                                            <option value="">Select Visa Category</option>
                                            {getVisaCategories(visaForm.country).map(c => <option key={c}>{c}</option>)}
                                        </select>
                                    </div>

                                    <div className="space-y-2 md:col-span-2">
                                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider">Visa Timeline Check</label>
                                        <select
                                            value={visaForm.timeline}
                                            onChange={(e) => setVisaForm({ ...visaForm, timeline: e.target.value as VisaTimeline })}
                                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded text-[#011E26] focus:border-[#009B8F] focus:outline-none"
                                        >
                                            <option>12+ months</option>
                                            <option>6-12 months</option>
                                            <option>3-6 months</option>
                                            <option>Less than 3 months</option>
                                            <option>Expiring soon / expired</option>
                                            <option>Not sure (AI will calculate)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2 pt-4 border-t border-gray-100">
                                    <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider flex items-center gap-2">
                                        <AlertTriangle size={16} className="text-orange-500" />
                                        Previous Visa Refusal?
                                    </label>
                                    <div className="flex gap-4">
                                        <label className={`flex-1 p-3 rounded border cursor-pointer flex items-center justify-center gap-2 transition-all ${visaForm.refusal === 'No' ? 'bg-[#009B8F] border-[#009B8F] text-white' : 'bg-gray-50 border-gray-200 text-slate-500'}`}>
                                            <input type="radio" name="refusal" className="hidden" onClick={() => setVisaForm({ ...visaForm, refusal: 'No' })} />
                                            <CheckCircle size={16} /> No
                                        </label>
                                        <label className={`flex-1 p-3 rounded border cursor-pointer flex items-center justify-center gap-2 transition-all ${visaForm.refusal === 'Yes' ? 'bg-red-500 border-red-500 text-white' : 'bg-gray-50 border-gray-200 text-slate-500'}`}>
                                            <input type="radio" name="refusal" className="hidden" onClick={() => setVisaForm({ ...visaForm, refusal: 'Yes' })} />
                                            <AlertTriangle size={16} /> Yes
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={handleRunPrediction}
                            className="w-full mt-8 py-4 bg-[#011E26] hover:bg-[#009B8F] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2"
                        >
                            <Activity size={20} /> Run AI Prediction
                        </button>

                    </div>
                ) : (
                    <div className="space-y-6 animate-fadeIn">
                        {/* RESULTS HEADER */}
                        <div className="flex items-center justify-between pb-6 border-b border-gray-100">
                            <div>
                                <h2 className="text-2xl font-serif text-[#011E26] flex items-center gap-3">
                                    <Shield className="text-[#009B8F]" /> AI Compliance Report
                                </h2>
                                <p className="text-slate-500 mt-1">Generated based on your provided data.</p>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-slate-500 uppercase tracking-widest">Overall Score</div>
                                <div className={`text-3xl font-bold ${(activeTab === 'student' ? studentResult?.score : visaResult?.score) > 60 ? 'text-[#009B8F]' : 'text-orange-500'
                                    }`}>
                                    {activeTab === 'student' ? studentResult?.score : visaResult?.score}/100
                                </div>
                            </div>
                        </div>

                        {/* STUDENT RESULTS CONTENT */}
                        {activeTab === 'student' && studentResult && (
                            <div className="space-y-6">
                                {/* Passport Section */}
                                <div className={`p-4 rounded border-l-4 ${studentResult.passportStatus === 'Good' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'}`}>
                                    <h4 className={`font-bold flex items-center gap-2 ${studentResult.passportStatus === 'Good' ? 'text-green-700' : 'text-orange-700'}`}>
                                        1. Passport Compliance: {studentResult.passportStatus}
                                    </h4>
                                    <p className="text-slate-700 text-sm mt-1">{studentResult.passportMsg}</p>
                                </div>

                                {/* Academic Readiness */}
                                <div className="p-4 rounded border-l-4 border-blue-500 bg-blue-50">
                                    <h4 className="font-bold text-blue-800 flex items-center gap-2">2. Academic Readiness: {studentResult.englishStatus}</h4>
                                    <p className="text-slate-700 text-sm mt-1">{studentResult.englishMsg}</p>
                                </div>

                                {/* Financial Readiness */}
                                <div className="p-4 rounded border-l-4 border-purple-500 bg-purple-50">
                                    <h4 className="font-bold text-purple-800 flex items-center gap-2">3. Financial Readiness: {studentResult.financialStatus}</h4>
                                    <p className="text-slate-700 text-sm mt-1">{studentResult.financialMsg}</p>
                                </div>

                                {/* Document Completeness */}
                                <div className="p-4 rounded border-l-4 border-slate-500 bg-slate-50">
                                    <h4 className="font-bold text-slate-800 flex items-center gap-2">4. Document Completeness: {studentResult.documentStatus}</h4>
                                    <p className="text-slate-700 text-sm mt-1">{studentResult.documentMsg}</p>
                                </div>
                            </div>
                        )}

                        {/* VISA RESULTS CONTENT */}
                        {activeTab === 'visa' && visaResult && (
                            <div className="space-y-6">
                                <div className={`p-4 rounded border-l-4 ${visaResult.timelineStatus === 'Critical' ? 'border-red-500 bg-red-50' : 'border-yellow-500 bg-yellow-50'}`}>
                                    <h4 className={`font-bold mb-1 ${visaResult.timelineStatus === 'Critical' ? 'text-red-800' : 'text-yellow-800'}`}>1. Visa Timeline Assessment: {visaResult.timelineStatus}</h4>
                                    <p className="text-slate-700 text-sm">{visaResult.timelineMsg}</p>
                                </div>

                                <div className="p-4 rounded border-l-4 border-blue-500 bg-blue-50">
                                    <h4 className="font-bold text-blue-800 mb-1">2. Eligibility Indicators: {visaResult.eligibilityStatus}</h4>
                                    <p className="text-slate-700 text-sm">{visaResult.eligibilityMsg}</p>
                                </div>

                                <div className={`p-4 rounded border-l-4 ${visaResult.riskLevel === 'High' ? 'border-red-600 bg-red-50' : 'border-slate-500 bg-slate-100'}`}>
                                    <h4 className={`font-bold mb-1 ${visaResult.riskLevel === 'High' ? 'text-red-800' : 'text-slate-800'}`}>3. Risk Assessment: {visaResult.riskLevel}</h4>
                                    <p className="text-slate-700 text-sm">{visaResult.riskMsg}</p>
                                </div>
                            </div>
                        )}

                        {/* AI RECOMMENDATION FOOTER */}
                        <div className="mt-8 pt-6 border-t border-gray-100">
                            <h3 className="text-lg font-serif text-[#009B8F] mb-3 flex items-center gap-2">
                                <Activity size={20} /> AI Recommendation
                            </h3>
                            <p className="text-slate-600 text-sm leading-relaxed mb-6">
                                {activeTab === 'student' ? studentResult?.recommendation : visaResult?.recommendation}
                            </p>                            {!showSubmitForm ? (
                                <div className="grid grid-cols-1 gap-3 mt-4">
                                    <button
                                        onClick={() => setShowSubmitForm(true)}
                                        className="w-full bg-[#009B8F] hover:bg-[#00857a] text-white px-6 py-4 rounded-lg font-bold uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-3"
                                    >
                                        <Scale size={18} /> Get Professional Review for this Profile
                                    </button>
                                    <button
                                        onClick={() => navigate('/signup')}
                                        className="w-full py-4 bg-white border border-gray-200 text-[#011E26] font-bold rounded hover:border-[#009B8F] transition-all flex items-center justify-center gap-2"
                                    >
                                        <Lock size={16} /> Sign Up for Unlimited Access
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-6">
                                    <AdvisorContactForm 
                                        serviceType="visa"
                                        onCancel={() => setShowSubmitForm(false)}
                                        initialData={{
                                            compliance_type: activeTab,
                                            ai_score: activeTab === 'student' ? studentResult?.score : visaResult?.score,
                                            ai_risk: activeTab === 'student' ? studentResult?.riskLevel : visaResult?.riskLevel
                                        }}
                                    />
                                </div>
                            )}

                            {/* LOCKED FEATURES REMOVED / MOVED INSIDE */}
                        </div>

                    </div>
                )}
            </div>

        </div>
    );
};

export default VisaComplianceHub;
